"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, lazy, Suspense, useContext, useEffect, useMemo, useRef, useState } from "react";
import { isWalletProviderDescriptor } from "../types";
import { useSettingsState } from "./settings";
import VaulDrawer from "../components/Modal/vaulModal";
import IconButton from "../components/Buttons/iconButton";
import { ChevronLeft } from "lucide-react";
import { useConnectModal } from "../components/Wallet/WalletModal";
import { isMobile } from "../lib/wallets/utils/isMobile";
import AppSettings from "../lib/AppSettings";
import { filterSourceNetworks } from "../helpers/filterSourceNetworks";
import { createWalletProvidersRegistry } from "../lib/walletConnect/walletProvidersRegistry";
import { createDescriptorStubStore } from "../lib/walletConnect/descriptorStubStore";
import { useWalletDescriptorLoader } from "../lib/walletConnect/walletDescriptorLoader";
import { ensureRegistryBrowseLoaded } from "../lib/walletConnect/additionalConnectorsStore";
import clsx from "clsx";
const ConnectorsList = lazy(() => import("../components/Wallet/WalletModal/ConnectorsList"));
// Shown while the connectors chunk loads on first modal open, so a slow chunk
// fetch surfaces a spinner instead of falling through to the error boundary.
const ConnectorsListFallback = () => (_jsx("div", { className: "flex h-full w-full items-center justify-center py-10", children: _jsx("div", { className: "loader text-[3px]!" }) }));
const WalletProvidersRegistryContext = createContext(null);
export function useWalletProvidersRegistry() {
    const registry = useContext(WalletProvidersRegistryContext);
    if (!registry)
        throw new Error('useWalletProvidersRegistry must be used within WalletProvidersProvider');
    return registry;
}
const WalletProvidersReadyContext = createContext(false);
export function useWalletProvidersReady() {
    return useContext(WalletProvidersReadyContext);
}
const HasConfiguredWalletProvidersContext = createContext(false);
/**
 * Whether the integrator configured any wallet-connection providers. Unlike the
 * registry (populated in a post-commit effect) this is derived from the
 * `walletProviders` prop during render, so it is correct on the very first
 * render and during SSR. Use it for decisions that only need "is wallet
 * connection available at all" (e.g. the deposit flow's root step); decisions
 * that need hydrated provider details must still gate on
 * `useWalletProvidersReady()`.
 */
export function useHasConfiguredWalletProviders() {
    return useContext(HasConfiguredWalletProvidersContext);
}
export const WalletProvidersProvider = ({ children, walletProviders }) => {
    const settings = useSettingsState();
    const { networks } = settings;
    const isMobilePlatform = isMobile();
    const { goBack, onFinish, open, setOpen, presentation, selectedConnector, selectedMultiChainConnector, dismissible, topContent, fullHeight, hideHeader } = useConnectModal();
    const walletProvidersRegistry = useMemo(() => createWalletProvidersRegistry(), []);
    const { loadAll } = useWalletDescriptorLoader();
    // Mirrors exactly which providers will produce a registry entry below
    // (descriptors get stubs; real providers need `createConnection`), but is
    // available at render time — before the effect publishes the entries.
    const hasConfiguredProviders = useMemo(() => walletProviders.some(p => isWalletProviderDescriptor(p) || !!p.createConnection), [walletProviders]);
    // Per-id caches: keep real connections alive across re-renders so that
    // a descriptor finishing its load doesn't tear down peer providers.
    const connectionsRef = useRef(new Map());
    const stubsRef = useRef(new Map());
    const [isInitialized, setIsInitialized] = useState(false);
    useEffect(() => {
        const seenIds = new Set();
        const entries = [];
        for (const p of walletProviders) {
            seenIds.add(p.id);
            if (isWalletProviderDescriptor(p)) {
                // Descriptor still pending: serve a static-metadata stub so
                // route filtering and the registry see the provider exists.
                let stub = stubsRef.current.get(p.id);
                if (!stub) {
                    stub = createDescriptorStubStore(p);
                    stubsRef.current.set(p.id, stub);
                }
                entries.push({ id: p.id, store: stub });
                continue;
            }
            // Real provider: drop any prior stub for this id, then init
            // a connection if we don't already have one.
            stubsRef.current.delete(p.id);
            let conn = connectionsRef.current.get(p.id);
            if (!conn && p.createConnection) {
                conn = p.createConnection({ networks, walletProvidersRegistry });
                connectionsRef.current.set(p.id, conn);
            }
            if (conn)
                entries.push({ id: p.id, store: conn.store });
        }
        // Tear down anything that disappeared from the input.
        for (const [id, conn] of connectionsRef.current) {
            if (!seenIds.has(id)) {
                conn.destroy?.();
                connectionsRef.current.delete(id);
            }
        }
        for (const id of Array.from(stubsRef.current.keys())) {
            if (!seenIds.has(id))
                stubsRef.current.delete(id);
        }
        walletProvidersRegistry.setEntries(entries);
        setIsInitialized(true);
    }, [walletProviders, walletProvidersRegistry]);
    useEffect(() => () => {
        // On unmount, dispose every still-live connection.
        for (const conn of connectionsRef.current.values())
            conn.destroy?.();
        connectionsRef.current.clear();
        stubsRef.current.clear();
        walletProvidersRegistry.setEntries([]);
    }, [walletProvidersRegistry]);
    useEffect(() => {
        connectionsRef.current.forEach(c => c.updateProps?.({ networks, walletProvidersRegistry }));
    }, [networks, walletProvidersRegistry]);
    // `AvailableSourceNetworkTypes` is read by `helpers/routes.ts` to decide
    // which source-network types are reachable. It depends on each provider's
    // current `withdrawalSupportedNetworks` plus the connected wallets, so it
    // must be refreshed whenever any provider's state moves.
    useEffect(() => {
        const recompute = () => {
            const snapshots = walletProvidersRegistry.getEntries().map(e => e.store.getState());
            const filtered = snapshots.filter(provider => (isMobilePlatform ? !provider.unsupportedPlatforms?.includes('mobile') : !provider.unsupportedPlatforms?.includes('desktop')) &&
                networks.some(net => provider.autofillSupportedNetworks?.includes(net.name) ||
                    provider.withdrawalSupportedNetworks?.includes(net.name) ||
                    provider.asSourceSupportedNetworks?.includes(net.name)));
            AppSettings.AvailableSourceNetworkTypes = filterSourceNetworks(settings, filtered);
        };
        recompute();
        return walletProvidersRegistry.subscribe(recompute);
    }, [settings, networks, isMobilePlatform, walletProvidersRegistry]);
    // Phase-1 trigger: hydrate every pending descriptor the first time the
    // connect modal opens, and warm the page-1 WalletConnect registry fetch
    // for every namespace store — twice, because descriptor hydration
    // instantiates stores that didn't exist when the modal opened. Later
    // phases can add finer-grained triggers (idle prefetch of connected
    // families, swap-page hydration, etc.).
    useEffect(() => {
        if (!open)
            return;
        ensureRegistryBrowseLoaded();
        void loadAll().then(() => ensureRegistryBrowseLoaded());
    }, [open, loadAll]);
    return (_jsx(WalletProvidersRegistryContext.Provider, { value: walletProvidersRegistry, children: _jsx(WalletProvidersReadyContext.Provider, { value: isInitialized, children: _jsxs(HasConfiguredWalletProvidersContext.Provider, { value: hasConfiguredProviders, children: [children, _jsx(VaulDrawer, { show: open && presentation === 'modal', setShow: setOpen, onClose: onFinish, modalId: "connectNewWallet", dismissible: dismissible, header: _jsxs("div", { className: "flex items-center gap-1", children: [(selectedConnector || selectedMultiChainConnector) ?
                                    _jsx("div", { className: "sm:-ml-2 ml-0", children: _jsx(IconButton, { onClick: goBack, icon: _jsx(ChevronLeft, { className: "h-6 w-6" }) }) })
                                    : null, hideHeader ? undefined : _jsx("p", { children: (selectedMultiChainConnector && !selectedConnector) ? "Select ecosystem" : "Connect wallet" })] }), children: _jsx(VaulDrawer.Snap, { openFullHeight: true, id: 'item-1', className: clsx("h-full max-h-[83svh] sm:max-h-full", fullHeight && "openpicker"), children: open ? (_jsxs("div", { className: "flex flex-col gap-3 h-full", children: [!selectedConnector && !selectedMultiChainConnector ? topContent : null, _jsx("div", { className: "flex-1 min-h-0", children: _jsx(Suspense, { fallback: _jsx(ConnectorsListFallback, {}), children: _jsx(ConnectorsList, { onFinish: onFinish }) }) })] })) : null }) })] }) }) }));
};
